import React, { useEffect, useRef, useState } from "react";

const SEVERITY_COLORS = {
  critical: "#E11D48",
  high: "#EA580C",
  medium: "#D97706",
  low: "#059669",
};

const MAPPLS_SDK_KEY = process.env.REACT_APP_MAPPLS_MAP_SDK_KEY;
const MAPPLS_SCRIPT_ID = "mappls-web-maps-sdk";
let mapplsSdkPromise = null;

function normalizeCoords(value) {
  if (!Array.isArray(value) || value.length < 2) return null;
  const lat = Number(value[0]);
  const lng = Number(value[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
}

function buildIconUrl(color, type) {
  if (type === "ambulance") {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <circle cx="24" cy="24" r="18" fill="${color}" opacity="0.25"/>
        <circle cx="24" cy="24" r="11" fill="${color}" stroke="white" stroke-width="3"/>
        <text x="24" y="29" font-size="16" text-anchor="middle">🚑</text>
      </svg>
    `)}`;
  }

  if (type === "hospital") {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <circle cx="24" cy="24" r="18" fill="${color}" opacity="0.25"/>
        <circle cx="24" cy="24" r="11" fill="${color}" stroke="white" stroke-width="3"/>
        <text x="24" y="29" font-size="16" text-anchor="middle">🏥</text>
      </svg>
    `)}`;
  }

  if (type === "patient") {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <circle cx="24" cy="24" r="18" fill="${color}" opacity="0.25"/>
        <circle cx="24" cy="24" r="11" fill="${color}" stroke="white" stroke-width="3"/>
        <text x="24" y="29" font-size="16" text-anchor="middle">🚗</text>
      </svg>
    `)}`;
  }

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
      <circle cx="24" cy="24" r="18" fill="${color}" opacity="0.25"/>
      <circle cx="24" cy="24" r="9" fill="${color}" stroke="white" stroke-width="3"/>
    </svg>
  `)}`;
}

function buildPopupHtml(marker) {
  const title = marker?.title || marker?.id || "Marker";
  const subtitle = marker?.subtitle
    ? `<div style="font-size:12px;color:#64748B;margin-top:2px;">${marker.subtitle}</div>`
    : "";
  const severity = marker?.severity
    ? `<div style="margin-top:6px;font-size:11px;text-transform:uppercase;color:${SEVERITY_COLORS[marker.severity] || "#2563EB"};font-weight:700;">${marker.severity}</div>`
    : "";

  return `
    <div style="font-family:Inter,system-ui,sans-serif;min-width:180px;">
      <div style="font-weight:700;font-size:13px;color:#0F172A;">${title}</div>
      ${subtitle}
      ${severity}
    </div>
  `;
}

function getSdkKey() {
  return MAPPLS_SDK_KEY.trim();
}

function loadMapplsSdk() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Mappls SDK can only load in the browser."));
  }

  if (window.mappls) {
    return Promise.resolve(window.mappls);
  }

  if (mapplsSdkPromise) {
    return mapplsSdkPromise;
  }

  const sdkKey = getSdkKey();
  if (!sdkKey) {
    return Promise.reject(new Error("Missing Mappls SDK key."));
  }

  mapplsSdkPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(MAPPLS_SCRIPT_ID);
    if (existingScript && window.mappls) {
      resolve(window.mappls);
      return;
    }

    const callbackName = `__mapplsMapViewInit_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    window[callbackName] = () => {
      try {
        if (window.mappls) {
          resolve(window.mappls);
        } else {
          reject(new Error("Mappls SDK loaded without exposing the global API."));
        }
      } finally {
        try {
          delete window[callbackName];
        } catch (_) {
          window[callbackName] = undefined;
        }
      }
    };

    const script = document.createElement("script");
    script.id = MAPPLS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://apis.mappls.com/advancedmaps/api/${encodeURIComponent(sdkKey)}/map_sdk?layer=vector&v=3.0&callback=${callbackName}`;
    script.onerror = () => {
      try {
        delete window[callbackName];
      } catch (_) {
        window[callbackName] = undefined;
      }
      mapplsSdkPromise = null;
      reject(new Error("Failed to load the Mappls Web Maps SDK."));
    };

    document.head.appendChild(script);
  });

  return mapplsSdkPromise;
}

function safeCall(target, methods, ...args) {
  if (!target) return false;
  for (const methodName of methods) {
    const method = target[methodName];
    if (typeof method === "function") {
      try {
        method.apply(target, args);
        return true;
      } catch (_) {}
    }
  }
  return false;
}

function destroyOverlay(overlay) {
  if (!overlay) return;
  safeCall(overlay, ["remove", "destroy", "setMap"], null);
}

function createMarker(mappls, map, markerData) {
  if (!mappls?.Marker) return null;

  const coords = normalizeCoords(markerData?.coords);
  if (!coords) return null;
  const latLngPosition = { lat: coords[0], lng: coords[1] };

  const markerOptions = {
    map,
    position: latLngPosition,
    icon: buildIconUrl(SEVERITY_COLORS[markerData?.severity] || "#2563EB", markerData?.type),
    popupHtml: buildPopupHtml(markerData),
    draggable: false,
  };

  const attempts = [
    () => new mappls.Marker(markerOptions),
    () => new mappls.Marker({ ...markerOptions, iconUrl: markerOptions.icon }),
    () => new mappls.Marker({ ...markerOptions, html: buildPopupHtml(markerData) }),
    () => new mappls.Marker({ map, position: [coords[0], coords[1]] }),
    () => new mappls.Marker({ map, position: [coords[1], coords[0]] }),
  ];

  for (const attempt of attempts) {
    try {
      return attempt();
    } catch (_) {}
  }

  return null;
}

function bindMarkerClick(mappls, map, marker, markerData, onMarkerClick) {
  if (typeof onMarkerClick !== "function") return;

  const popupHtml = buildPopupHtml(markerData);

  const openPopup = () => {
    if (mappls?.InfoWindow) {
      try {
        const infoWindow = new mappls.InfoWindow({ content: popupHtml });
        if (typeof infoWindow.open === "function") {
          infoWindow.open(map, marker);
          return;
        }
        if (typeof infoWindow.setContent === "function") {
          infoWindow.setContent(popupHtml);
        }
      } catch (_) {}
    }

    onMarkerClick(markerData);
  };

  const bound =
    safeCall(marker, ["addListener"], "click", openPopup) ||
    safeCall(marker, ["on"], "click", openPopup) ||
    safeCall(marker, ["addEventListener"], "click", openPopup);

  if (!bound) {
    onMarkerClick(markerData);
  }
}

function createPolyline(mappls, map, route) {
  if (!mappls?.Polyline || !Array.isArray(route) || route.length < 2) return null;

  const polylineOptions = {
    map,
    path: route,
    strokeColor: "#3b82f6",
    strokeOpacity: 1,
    strokeWeight: 5,
  };

  const attempts = [
    () => new mappls.Polyline(polylineOptions),
    () => new mappls.Polyline({ ...polylineOptions, paths: route }),
  ];

  for (const attempt of attempts) {
    try {
      return attempt();
    } catch (_) {}
  }

  return null;
}

function fitMapToPoints(map, points) {
  const validPoints = points.map(normalizeCoords).filter(Boolean);
  if (!validPoints.length) return;

  if (validPoints.length === 1) {
    const [lat, lng] = validPoints[0];
    safeCall(map, ["setCenter", "panTo", "setView"], { lat, lng }) ||
      safeCall(map, ["setCenter", "panTo", "setView"], lat, lng) ||
      safeCall(map, ["setCenter", "panTo", "setView"], [lat, lng]) ||
      safeCall(map, ["setCenter", "panTo", "setView"], [lng, lat]);
    return;
  }

  const bounds = validPoints.reduce(
    (acc, [lat, lng]) => ({
      minLat: Math.min(acc.minLat, lat),
      maxLat: Math.max(acc.maxLat, lat),
      minLng: Math.min(acc.minLng, lng),
      maxLng: Math.max(acc.maxLng, lng),
    }),
    {
      minLat: validPoints[0][0],
      maxLat: validPoints[0][0],
      minLng: validPoints[0][1],
      maxLng: validPoints[0][1],
    }
  );

  const southWestLatLng = { lat: bounds.minLat, lng: bounds.minLng };
  const northEastLatLng = { lat: bounds.maxLat, lng: bounds.maxLng };
  const southWestLngLat = [bounds.minLng, bounds.minLat];
  const northEastLngLat = [bounds.maxLng, bounds.maxLat];

  const fitted =
    safeCall(map, ["fitBounds"], [southWestLatLng, northEastLatLng], { padding: 50 }) ||
    safeCall(map, ["fitBounds"], [southWestLngLat, northEastLngLat], { padding: 50 });

  if (!fitted) {
    const midLat = (bounds.minLat + bounds.maxLat) / 2;
    const midLng = (bounds.minLng + bounds.maxLng) / 2;
    safeCall(map, ["setCenter", "panTo", "setView"], { lat: midLat, lng: midLng }) ||
      safeCall(map, ["setCenter", "panTo", "setView"], midLat, midLng) ||
      safeCall(map, ["setCenter", "panTo", "setView"], [midLat, midLng]) ||
      safeCall(map, ["setCenter", "panTo", "setView"], [midLng, midLat]);
  }
}

function ensureMapCenter(map, centerArr) {
  if (!Array.isArray(centerArr) || centerArr.length < 2) return false;
  const lat = Number(centerArr[0]);
  const lng = Number(centerArr[1]);

  return !!(
    safeCall(map, ["setCenter", "panTo", "setView"], { lat, lng }) ||
    safeCall(map, ["setCenter", "panTo", "setView"], lat, lng) ||
    safeCall(map, ["setCenter", "panTo", "setView"], [lat, lng]) ||
    safeCall(map, ["setCenter", "panTo", "setView"], [lng, lat])
  );
}

export default function MapView({
  markers = [],
  route = null,
  center = [19.076, 72.8777],
  focusPoint = null,
  zoom = 11,
  height = 420,
  testId = "map-view",
  onMarkerClick,
}) {
  const containerRef = useRef(null);
  const containerIdRef = useRef(`mappls-container-${Math.random().toString(36).slice(2, 9)}`);
  const mapRef = useRef(null);
  const mapplsRef = useRef(null);
  const overlaysRef = useRef([]);
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const focusCoords = normalizeCoords(focusPoint);

  useEffect(() => {
    let cancelled = false;

    loadMapplsSdk()
      .then((mappls) => {
        if (cancelled) return;
        mapplsRef.current = mappls;
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorMessage(err?.message || "Failed to load the Mappls Web Maps SDK.");
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (status !== "ready") return;
    if (!containerRef.current || !mapplsRef.current || mapRef.current) return;

    const mappls = mapplsRef.current;
    const containerId = containerIdRef.current;

    let map = null;
    const initialCenter = focusCoords || center;

    const attempts = [
      () => new mappls.Map(containerId, { center: { lat: initialCenter[0], lng: initialCenter[1] }, zoom }),
      () => new mappls.Map(containerId, { center: [initialCenter[1], initialCenter[0]], zoom }),
      () => new mappls.Map(containerId, { center: initialCenter, zoom }),
      () => new mappls.Map(containerRef.current, { center: { lat: initialCenter[0], lng: initialCenter[1] }, zoom }),
      () => new mappls.Map(containerRef.current, { center: initialCenter, zoom }),
    ];

    for (const attempt of attempts) {
      try {
        map = attempt();
        if (map) break;
      } catch (e) {}
    }

    if (!map) {
      setErrorMessage("Failed to initialize the Mappls map with available constructor signatures.");
      setStatus("error");
      return;
    }

    mapRef.current = map;
    ensureMapCenter(map, initialCenter);
    safeCall(map, ["setZoom", "zoomTo"], zoom);

    const timer = setTimeout(() => {
      safeCall(map, ["invalidateSize", "resize"]);
    }, 50);

    return () => {
      clearTimeout(timer);
    };
  }, [center, focusCoords, zoom, status]);

  useEffect(() => {
    const map = mapRef.current;
    const mappls = mapplsRef.current;
    if (!map || !mappls) return;

    if (focusCoords) {
      ensureMapCenter(map, focusCoords);
      safeCall(map, ["setZoom", "zoomTo"], Math.max(14, zoom));
      return undefined;
    }

    overlaysRef.current.forEach(destroyOverlay);
    overlaysRef.current = [];

    if (Array.isArray(route) && route.length > 1) {
      const polyline = createPolyline(mappls, map, route);
      if (polyline) overlaysRef.current.push(polyline);
      fitMapToPoints(map, route);
    } else {
      const markerPoints = markers.map((marker) => normalizeCoords(marker?.coords)).filter(Boolean);

      if (markerPoints.length > 0) {
        if (markerPoints.length === 1) {
          ensureMapCenter(map, markerPoints[0]);
          safeCall(map, ["setZoom"], zoom);
        } else {
          fitMapToPoints(map, markerPoints);
        }
      } else {
        ensureMapCenter(map, center);
        safeCall(map, ["setZoom"], zoom);
      }
    }

    markers.forEach((markerData) => {
      const marker = createMarker(mappls, map, markerData);
      if (!marker) return;
      overlaysRef.current.push(marker);
      bindMarkerClick(mappls, map, marker, markerData, onMarkerClick);
    });

    return undefined;
  }, [center, focusCoords, markers, onMarkerClick, route, zoom, status]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || status !== "ready" || (Array.isArray(route) && route.length > 1) || focusCoords) return;

    const markerPoints = (Array.isArray(markers) ? markers : [])
      .map((marker) => normalizeCoords(marker?.coords))
      .filter(Boolean);
    if (markerPoints.length > 0) return;

    ensureMapCenter(map, center);
    safeCall(map, ["setZoom"], zoom);
  }, [center, focusCoords, markers, route, status, zoom]);

  useEffect(() => {
    return () => {
      overlaysRef.current.forEach(destroyOverlay);
      overlaysRef.current = [];

      if (mapRef.current) {
        safeCall(mapRef.current, ["remove", "destroy", "dispose"]);
        mapRef.current = null;
      }
    };
  }, []);

  const showFallback = status !== "ready";

  return (
    <div
      data-testid={testId}
      className="relative w-full overflow-hidden rounded-2xl border border-slate-200 shadow-inner bg-slate-50"
      style={{ height }}
    >
      {showFallback && (
        <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
          <div className="max-w-sm space-y-2 rounded-xl border border-slate-200 bg-white/95 px-4 py-3 shadow-sm">
            <p className="text-sm font-semibold text-slate-900">
              {status === "error" ? "Map failed to load" : "Loading Mappls map…"}
            </p>
            <p className="text-xs text-slate-600">
              {status === "error"
                ? errorMessage || "The Mappls SDK could not be loaded."
                : "Initializing the map experience."}
            </p>
          </div>
        </div>
      )}
      <div
        id={containerIdRef.current}
        ref={containerRef}
        className="h-full w-full"
        style={{ opacity: showFallback ? 0 : 1 }}
      />
    </div>
  );
}